import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Box } from '../../../UI/Box'
import { ProgressBar } from './styles'
import { selectTaskSubmissions } from '../../../../modules/missionControl/selectors'
import {
  Frequency,
  findEarliestViableCompletionPeriod,
  getRecurringTimeLeft,
  getRecurringTimeLeftByFrequencyAndPeriodStartDate,
  getResetDateByFrequency,
  getStartDateByFrequency,
} from '../../../../utils/recurring'
import ThemedButton from '../../../ThemedButton'
import { Task } from '../../../../page_components/PoolDetails/Missions/types'
import { DesktopView } from '../../../Pools/PoolCarousel/styles'
import { Info } from '../../styles'
import { useGetFormattedTaskRewardLabels } from '../../TaskAllocationAmount'
import TaskDescription from '../../TaskDescription'
import SharedTaskCard from '../../../Library/Card/SharedTaskCard'
import { getMissionTaskType } from '../../MissionAction'

export const Icon = styled.img<{ smallMargin?: boolean }>`
  width: 18px;
  height: 18px;
`

export const ResetInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`

export type Props = {
  task: Task
  isComplete: boolean
  projectStatus?: 'ended' | 'active' | 'coming-soon'
  actionText: string
  onClick: () => void
  rewardDisplay?: string
  isExpired?: boolean
}

const RecurringTask = ({
  task,
  isComplete,
  projectStatus,
  actionText,
  onClick,
  rewardDisplay,
  isExpired,
}: Props) => {
  const taskSubmissions = useSelector(selectTaskSubmissions)
  const { pointReward, internalName: description, multiplierReward } = task
  const subtaskId = task?.subtasks?.[0]?.id
  const submissions = taskSubmissions?.[subtaskId]

  const { isZapLaunchMission } = getMissionTaskType({ task })

  const submissionsInCurrentPeriod = useMemo(() => {
    // seeing as this is a fixed time period from the first submission rather than a calendar date, we're going to do the logic seperately. In future if we use more of these types of recurring missions we can abstract this out
    if (isZapLaunchMission) {
      const earliestCompletionPeriod = findEarliestViableCompletionPeriod(task.frequency)

      const filteredSubmissions = submissions?.filter((submission) => {
        const submissionDate = new Date(submission.createdAt)
        return submissionDate >= earliestCompletionPeriod
      })

      return filteredSubmissions
    }

    const startDate = new Date(getStartDateByFrequency(task.frequency))
    const endDate = new Date(getResetDateByFrequency(task.frequency))
    return submissions?.filter((submission) => {
      const submissionDate = new Date(submission.createdAt)
      return submissionDate >= startDate && submissionDate <= endDate
    })
  }, [submissions, task.frequency, isZapLaunchMission])

  
  const sortedSubmissionsByCompletionCount = [...(submissionsInCurrentPeriod ?? [])]?.sort((a, b) => {
    if (a.completionsCount && b.completionsCount) {
      return b.completionsCount - a.completionsCount
    }
    return 0
  })

  const numberOfCompletions = submissionsInCurrentPeriod?.length ?? 0
  const allTimeRecurringRewards = (submissions?.length ?? 0) * pointReward

  const getFormattedReward = useGetFormattedTaskRewardLabels({
    taskId: task.id,
  })
  const { formattedReward } = getFormattedReward({ rewardDisplay })
  const { formattedReward: allTimeFormattedReward } = getFormattedReward({
    overrideReward: allTimeRecurringRewards,
    includePlus: false,
  })

  const isDisabled = isComplete || projectStatus === 'ended' || isExpired

  const recurringDetails = {
    numberOfCompletions,
    allowedCompletionsPerPeriod: task.completionsPerPeriod,
    allTimeRecurringRewards,
  }

  let recurringTimeLeft = getRecurringTimeLeft(task.frequency)
  if (isZapLaunchMission) {
    const recurringPeriodStartDate = sortedSubmissionsByCompletionCount[0]?.periodStartDate ? new Date(sortedSubmissionsByCompletionCount[0].periodStartDate) : undefined
    recurringTimeLeft = getRecurringTimeLeftByFrequencyAndPeriodStartDate(task.frequency, recurringPeriodStartDate)
  }

  return (
    <SharedTaskCard
      task={task}
      isComplete={isComplete}
      projectStatus={projectStatus}
      rewardDisplay={rewardDisplay}
      onClick={onClick}
      actionText={actionText}
      isFaded={isDisabled}
      isRecurring={task.frequency !== Frequency.NONE}
      isExpired={isExpired}
      multiplier={multiplierReward}
      recurringTimeLeft={recurringTimeLeft}
      formattedReward={formattedReward}
      recurringDetails={recurringDetails}
    >
      <Box afs g={16}>
        <Box dc g={6} className="task-info">
          <Box g={40} ac>
            <Box dc g={6}>
              <TaskDescription>{description}</TaskDescription>
              <Box g={4} ac>
                <Info type="primary">{formattedReward}</Info>
                {task.completionsPerPeriod > 1 && (
                  <DesktopView>
                    <span>/ COMPLETION</span>
                  </DesktopView>
                )}
              </Box>
            </Box>
            {isDisabled && (
              <span>
                <Info type="primary" bordered>
                  {isComplete
                    ? 'COMPLETED'
                    : projectStatus === 'coming-soon'
                    ? 'SOON'
                    : 'EXPIRED'}
                </Info>
              </span>
            )}
            {!isDisabled && (
              <ThemedButton
                disabled={isDisabled}
                onClick={onClick}
                text={actionText}
                $styleType="primary"
              />
            )}
          </Box>
          {task.frequency !== Frequency.NONE && (
            <Box dc g={6}>
              {task.completionsPerPeriod > 1 && (
                <ProgressBar
                  length={task.completionsPerPeriod}
                  filled={numberOfCompletions}
                />
              )}
              <Box g={12} jsb ac>
                {allTimeRecurringRewards > 0 ? (
                  <Info type="primary">{allTimeFormattedReward}</Info>
                ) : (
                  <span />
                )}
                <ResetInfo>
                  <Icon
                    src="/img/icons/reset.svg"
                    alt="chain warning"
                    smallMargin
                  />
                  RESETS IN {getRecurringTimeLeft(task.frequency)}
                </ResetInfo>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </SharedTaskCard>
  )
}

export default RecurringTask
